import { inject, Injectable } from '@angular/core';
import type { BreakpointState } from '@angular/cdk/layout';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter, merge, of } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { PlatformService } from '@core/platform';

import { BREAKPOINTS } from './breakpoints/breakpoints';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const fallbackDeviceInfo: DeviceInfo = {
  isMobile: true,
  isTablet: false,
  isDesktop: false,
};

@Injectable({
  providedIn: 'root',
})
export class MediaObserverService {
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #detectorService = inject(DeviceDetectorService);
  readonly #platformService = inject(PlatformService);

  readonly #initialDeviceDetector$ = of(this.initialDeviceInfo());
  readonly #mediaObserver$ = this.#breakpointObserver
    .observe([
      BREAKPOINTS.XSmall,
      BREAKPOINTS.Small,
      BREAKPOINTS.Medium,
      BREAKPOINTS.Large,
      BREAKPOINTS.XLarge,
    ])
    .pipe(
      // on the SSR, we only care about the initialDeviceDetector$ value! If we enable this breakpointObserver
      // server-side, the result will always be { isMobile: false, isTablet: false, isDesktop: false }
      filter(() => this.#platformService.isClient()),
      debounceTime(10),
      map(
        ({ breakpoints }: BreakpointState): DeviceInfo => ({
          isMobile:
            breakpoints[BREAKPOINTS.XSmall] || breakpoints[BREAKPOINTS.Small],
          isTablet: breakpoints[BREAKPOINTS.Medium],
          isDesktop:
            breakpoints[BREAKPOINTS.Large] || breakpoints[BREAKPOINTS.XLarge],
        })
      )
    );

  /**
   * @deprecated Please use the deviceInfo signal from this service
   * @example
   * ```typescript
   * isDesktop = computed(() => this.#mediaObserverService.deviceInfo().isDesktop);
   * ```
   */
  public deviceInfo$ = merge(
    this.#initialDeviceDetector$,
    this.#mediaObserver$
  );

  public readonly deviceInfo = toSignal(this.deviceInfo$, {
    initialValue: fallbackDeviceInfo,
  });

  private initialDeviceInfo(): DeviceInfo {
    const { deviceType } = this.#detectorService.getDeviceInfo();

    // Fallback to mobile in case the device type is not known
    // NOTE: on the SSR the deviceType will try to be resolved based on the userAgent
    //       see: libs/core/server/src/lib/device-detector/universal-device-detector.ts
    if (deviceType === 'unknown') {
      return fallbackDeviceInfo;
    }

    return {
      isMobile: this.#detectorService.isMobile(),
      isTablet: this.#detectorService.isTablet(),
      isDesktop: this.#detectorService.isDesktop(),
    };
  }
}
